import React from "react";
import { PublicLayout } from "@/components/site/PublicLayout";

const SectionHeading = ({ id, children }) => (
  <h2
    id={id}
    className="mt-12 mb-4 font-display text-2xl sm:text-[26px] font-bold tracking-tight text-[color:var(--coh-ink)] scroll-mt-24"
  >
    {children}
  </h2>
);

export default function PrivacyPage() {
  return (
    <PublicLayout>
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-14 sm:pt-16 pb-20">
        <div className="kicker" data-testid="privacy-kicker">Privacy</div>
        <h1
          data-testid="privacy-heading"
          className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[color:var(--coh-ink)]"
        >
          Cohnect &mdash; Privacy Policy
        </h1>
        <p className="mt-4 text-sm text-muted-foreground" data-testid="privacy-effective-date">
          Effective date: 08/09/2026
        </p>
        <div className="accent-hairline mt-8 max-w-xs" />

        <div className="prose-editorial mt-10 text-[color:var(--coh-ink)]" data-testid="privacy-body">
          <p className="lead text-base sm:text-lg leading-relaxed text-muted-foreground">
            <span className="font-semibold text-[color:var(--coh-ink)]">The short version</span>{" "}
            (not a substitute for the full policy): Cohnect does not ask for your name, email
            address, or any account. There is no mailing list. When you tap &ldquo;I sent it&rdquo;
            on the site, or run the <code className="font-mono">/sent</code> command in our Discord
            community, we store a one-way scrambled code instead of anything that identifies you
            &mdash; enough to stop the same person counting twice, not enough to know who you are.
            Our community lives on Discord, which is an independent company with its own privacy
            practices we don&rsquo;t control.
          </p>

          <SectionHeading id="who-we-are">1. Who We Are</SectionHeading>
          <p>
            Cohnect (&ldquo;Cohnect,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;)
            is a free editorial publication operated by Cohnect LLC. This
            Privacy Policy explains how we handle information in connection with the website at
            cohnect.com and its features (the &ldquo;Service&rdquo;). Questions:{" "}
            <a href="mailto:info@cohnect.net" className="text-[color:var(--coh-blue)] hover:underline">info@cohnect.net</a>.
          </p>
          <p>
            This policy covers only the Service. It does not cover Discord Inc., any third-party
            website we link to, or any other service, each of which has its own privacy practices
            we neither control nor are responsible for.
          </p>
          <p>
            This policy applies to visitors in the United States and Canada (including Quebec). We
            have designated a privacy contact who is responsible for overseeing how we handle
            personal information and for answering privacy questions and requests:{" "}
            <strong>Privacy Officer &mdash; Cohnect LLC</strong>, reachable at{" "}
            <a href="mailto:info@cohnect.net" className="text-[color:var(--coh-blue)] hover:underline">info@cohnect.net</a>.
          </p>
          <p className="text-sm text-muted-foreground">
            <strong>A note about the Cohnect mobile app.</strong> The Cohnect mobile app is
            designed to be offline-first: the people, places, and things you add are stored locally
            on your own device, and the app does not send that content to our servers. This Privacy
            Policy describes the <em>website</em> at cohnect.com; the app does not collect
            personal information on a server under our control.
          </p>

          <SectionHeading id="not-collect">2. What We Do Not Collect</SectionHeading>
          <p>We want to be specific, because the absence is the point:</p>
          <ul>
            <li><strong>No email addresses.</strong> There is no newsletter signup, no mailing list, and no email delivery provider. We do not send email to readers.</li>
            <li><strong>No accounts.</strong> There is no registration, login, profile, or password for readers.</li>
            <li><strong>No names, phone numbers, addresses, or payment information.</strong> The Service is free and we accept no payments.</li>
            <li><strong>No comments, messages, or user-submitted content</strong> on the website.</li>
            <li><strong>No third-party advertising, ad networks, or advertising cookies</strong>, and we do not sell, rent, or share personal information with anyone for advertising or any other purpose.</li>
            <li><strong>No message contents.</strong> The prompts and templates we publish are used entirely on your own device, in your own messaging apps. Messages you write or send never pass through us, and we have no way to see them, store them, or know who you contacted.</li>
          </ul>

          <SectionHeading id="do-collect">3. What We Do Collect</SectionHeading>
          <p>
            <strong>a) Human Count events (website).</strong> When you tap the &ldquo;I sent it&rdquo;
            button, we record: the fact that an action was reported, which issue it relates to, the
            time, and a one-way cryptographic hash derived from your IP address combined with a
            secret value. The hash lets us prevent the same visitor from counting repeatedly for one
            issue. We do not store your IP address alongside it, and the hash cannot practically be
            reversed to identify you.
          </p>
          <p>
            <strong>b) Human Count events (Discord).</strong> When you use the{" "}
            <code className="font-mono">/sent</code> command in our community, Discord sends us your
            Discord user ID. We immediately convert it to a one-way hash combined with a secret
            value and store only that hash, plus the issue number and timestamp. We do not store
            your raw Discord ID, username, display name, avatar, messages, or any other Discord
            profile information.
          </p>
          <p>
            <strong>c) Server and technical logs.</strong> Like nearly all websites, our hosting
            provider automatically records standard technical information when pages are requested
            &mdash; such as IP address, browser type, requested page, referring page, and
            timestamp. These logs exist for security, abuse prevention, and reliability, are not
            used to build profiles of individuals, and are retained only for a short period in the
            ordinary course.
          </p>
          <p>
            <strong>d) Messages you choose to send us.</strong> If you email{" "}
            <a href="mailto:info@cohnect.net" className="text-[color:var(--coh-blue)] hover:underline">info@cohnect.net</a>,
            we receive whatever you include in that message and keep it as long as needed to
            respond and maintain our records.
          </p>

          <SectionHeading id="cookies">4. Cookies and Analytics</SectionHeading>
          <p>
            We do not use third-party analytics services, tracking pixels, advertising cookies, or
            cross-site trackers. We may use strictly necessary local storage or cookies for basic
            site function (for example, remembering that you already tapped &ldquo;I sent it&rdquo;
            on a page). We honor &ldquo;Do Not Track&rdquo; signals to the extent that we have
            nothing to track in the first place.
          </p>

          <SectionHeading id="why-process">5. Why We Process This Information</SectionHeading>
          <p>
            We process the limited information above to: display and operate the weekly issues;
            operate and protect the integrity of the Human Count; keep the Service secure and
            prevent abuse, spam, and automated manipulation; respond to messages you send us; and
            comply with legal obligations. Where the law requires a legal basis (such as the UK/EU
            GDPR), we rely on our legitimate interests in operating and securing a free
            publication, on your consent where you voluntarily submit information, and on legal
            obligation where applicable.
          </p>

          <SectionHeading id="sharing">6. Sharing</SectionHeading>
          <p>
            We do not sell or rent information, and we do not share it for advertising. We share
            information only with:
          </p>
          <ul>
            <li><strong>Service providers</strong> who operate the Service on our behalf &mdash; our website host and database provider &mdash; who process data solely to provide those services to us.</li>
            <li><strong>Legal recipients</strong>, if we are required to by law, subpoena, or valid legal process, or where we believe disclosure is reasonably necessary to protect our legal rights or the safety of any person.</li>
            <li><strong>A successor</strong>, if the Service is transferred to another operator, in which case this policy will continue to apply to the transferred information until updated.</li>
          </ul>

          <SectionHeading id="discord">7. Discord</SectionHeading>
          <p>
            Our community is hosted on Discord, an independent third-party platform. Discord Inc.
            is not affiliated with, partnered with, or endorsed by Cohnect, and we are not
            affiliated with or endorsed by Discord Inc.
          </p>
          <p>
            When you join and use our Discord community, your information is collected and
            processed by Discord under Discord&rsquo;s own Privacy Policy and Terms of Service,
            which govern your relationship with Discord, not with us. We do not control
            Discord&rsquo;s data practices, retention, or security.
          </p>
          <p>
            Within our community, we can see the same things any server operator can see &mdash;
            public messages in the server, member lists, and profile information you have made
            visible. We do not export, mine, or store that information, and the only data our
            systems retain from Discord is the hashed value described in Section 3(b). We are not
            responsible for information you choose to share with other members, including
            information shared in direct messages between members, which we cannot see.
          </p>

          <SectionHeading id="retention">8. Retention</SectionHeading>
          <p>
            Human Count events are retained indefinitely in hashed form because they constitute the
            running total; they contain no identifying information. Server logs are retained
            briefly for security purposes. Email correspondence is retained as long as reasonably
            needed.
          </p>

          <SectionHeading id="security">9. Security</SectionHeading>
          <p>
            We take reasonable technical measures to protect the limited information we hold,
            including hashing identifiers with a secret value, restricting administrative access to
            authenticated administrators, and serving the site over encrypted connections. No
            system is perfectly secure, and we cannot guarantee absolute security. Our best
            protection is that we hold almost nothing worth taking.
          </p>

          <SectionHeading id="your-rights">10. Your Rights</SectionHeading>
          <p>
            Depending on where you live (including under the GDPR, UK GDPR, and California privacy
            laws), you may have rights to access, correct, delete, or restrict processing of your
            personal information, to object to processing, to data portability, and to be free from
            discrimination for exercising these rights. California residents are entitled to know
            that we have not sold or shared personal information for cross-context behavioral
            advertising, and we do not process sensitive personal information.
          </p>
          <p>
            To exercise any right, email{" "}
            <a href="mailto:info@cohnect.net" className="text-[color:var(--coh-blue)] hover:underline">info@cohnect.net</a>.
            Please note an important practical limit: because Human Count records contain only
            irreversible hashes and no identifiers, we generally cannot locate, verify, or link any
            record to you, and therefore may be unable to fulfill access or deletion requests for
            that data. EU/UK residents have the right to lodge a complaint with a local supervisory
            authority.
          </p>

          <SectionHeading id="canada">11. Canada &amp; Quebec Residents (PIPEDA and Law 25)</SectionHeading>
          <p>
            For visitors in Canada, we handle personal information in accordance with the federal
            Personal Information Protection and Electronic Documents Act (PIPEDA) and, for Quebec
            residents, the Act respecting the protection of personal information in the private
            sector (Quebec&rsquo;s &ldquo;Law 25&rdquo;).
          </p>
          <ul>
            <li><strong>Accountability and contact.</strong> Our designated <strong>Privacy Officer &mdash; Cohnect LLC</strong> is responsible for our compliance and can be reached at <a href="mailto:info@cohnect.net" className="text-[color:var(--coh-blue)] hover:underline">info@cohnect.net</a>.</li>
            <li><strong>What we collect and why.</strong> As described above, the only information our systems retain is an irreversible, salted hash used to operate the Human Count, together with brief server logs and any email you choose to send us. We do not sell personal information and do not use it for advertising or profiling.</li>
            <li><strong>Consent.</strong> We rely on your consent, which for the Human Count you give by choosing to tap &ldquo;I sent it&rdquo; on the site or run the <code className="font-mono">/sent</code> command in Discord. You can decline simply by not using those features.</li>
            <li><strong>Access and correction.</strong> You may ask us to confirm what personal information we hold about you and to correct it, and you may withdraw consent at any time. As noted in Section 10, Human Count records contain only irreversible hashes with no identifiers, so we are generally unable to locate or link them to a specific person.</li>
            <li><strong>Cross-border transfer.</strong> We operate from, and store information in, the United States. By using the Service from Canada, you acknowledge that your information may be processed outside Canada, where it may be accessible to U.S. authorities under applicable law.</li>
            <li><strong>Automated decisions and profiling.</strong> We do not use personal information to make automated decisions that produce legal or similarly significant effects, and we do not build profiles of individuals.</li>
            <li><strong>Complaints.</strong> Please contact our Privacy Officer first. Canadian residents may also contact the Office of the Privacy Commissioner of Canada, and Quebec residents may contact the Commission d&rsquo;acc&egrave;s &agrave; l&rsquo;information du Qu&eacute;bec.</li>
          </ul>

          <SectionHeading id="children">12. Children</SectionHeading>
          <p>
            The Service is not directed to children under 13, and we do not knowingly collect
            personal information from children under 13. Our Discord community is intended for
            adults 18 and older and is additionally subject to Discord&rsquo;s own age
            requirements. If you believe a child has provided us with personal information, contact
            us and we will delete what we can identify.
          </p>

          <SectionHeading id="international">13. International Visitors</SectionHeading>
          <p>
            The Service is operated from the United States and is intended for visitors in the
            United States and Canada. If you access it from elsewhere, any information processed
            will be handled in the United States, where privacy laws may differ from those in your
            country or province. By using the Service, you understand that your information may be
            processed in the United States.
          </p>

          <SectionHeading id="changes">14. Changes to This Policy</SectionHeading>
          <p>
            We may update this Privacy Policy from time to time. We will post the revised version
            with a new effective date. Your continued use of the Service after changes are posted
            constitutes acceptance of the updated policy.
          </p>

          <SectionHeading id="contact">15. Contact</SectionHeading>
          <p>
            Cohnect LLC. For support, complaints, privacy questions, or requests, email{" "}
            <a href="mailto:info@cohnect.net" className="text-[color:var(--coh-blue)] hover:underline">info@cohnect.net</a>,
            call{" "}
            <a href="tel:+19416466999" className="text-[color:var(--coh-blue)] hover:underline">+1 941-646-6999</a>, or
            write to us at:
          </p>
          <p>
            Cohnect LLC<br />
            7901 4th St N #13824<br />
            St Petersburg, FL 33702<br />
            United States (Pinellas County)
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}
